/**
 * Modelo da proposta (D103): textos padrão do PDF, editáveis pelo admin em Parâmetros do sistema.
 * Marcadores trocados na geração: {validade}, {entrega}, {pagamento}. Cada linha vira um parágrafo.
 */
import { useEffect, useState } from "react";
import { useGetList, useNotify, usePermissions } from "react-admin";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { Permissoes } from "../lib/provedores";
import { supabase } from "../lib/supabase";

interface Modelo {
  id: string;
  declaracoes: string;
  validade_dias: number;
  representante_qualificacao: string | null;
  cidade_assinatura: string;
}

export function ModeloPropostaParametro() {
  const notify = useNotify();
  const { permissions } = usePermissions<Permissoes | null>();
  const admin = permissions?.papel === "admin";
  const { data: modelos = [], refetch } = useGetList<Modelo>("modelo_proposta", { pagination: { page: 1, perPage: 1 }, sort: { field: "criado_em", order: "ASC" } });
  const modelo = modelos[0];
  const [declaracoes, setDeclaracoes] = useState("");
  const [validade, setValidade] = useState("");
  const [qualificacao, setQualificacao] = useState("");
  const [cidade, setCidade] = useState("");
  const [gravando, setGravando] = useState(false);

  useEffect(() => {
    if (!modelo) return;
    setDeclaracoes(modelo.declaracoes);
    setValidade(String(modelo.validade_dias));
    setQualificacao(modelo.representante_qualificacao ?? "");
    setCidade(modelo.cidade_assinatura);
  }, [modelo?.id]);

  if (!modelo) return null;
  const validadeNum = Number(validade);
  const invalido = !declaracoes.trim() || !cidade.trim() || !Number.isInteger(validadeNum) || validadeNum <= 0;

  async function gravar() {
    if (!modelo) return;
    setGravando(true);
    const { error } = await supabase
      .from("modelo_proposta")
      .update({ declaracoes: declaracoes.trim(), validade_dias: validadeNum, representante_qualificacao: qualificacao.trim() || null, cidade_assinatura: cidade.trim() })
      .eq("id", modelo.id);
    setGravando(false);
    if (error) notify(`Não foi possível gravar: ${error.message}`, { type: "error" });
    else {
      notify("Modelo da proposta gravado.", { type: "success" });
      void refetch();
    }
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontSize: "1rem" }}>
          Modelo da proposta
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Textos que saem em toda proposta. Nas declarações, {"{validade}"}, {"{entrega}"} e {"{pagamento}"} são trocados pelos valores da proposta; cada linha vira
          um parágrafo. Dados da empresa, endereço e assinatura vêm de Dados da empresa.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField label="Declarações" value={declaracoes} onChange={(e) => setDeclaracoes(e.target.value)} fullWidth multiline minRows={6} disabled={!admin} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Validade padrão (dias)"
              value={validade}
              onChange={(e) => setValidade(e.target.value.replace(/\D/g, ""))}
              fullWidth
              disabled={!admin}
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField label="Cidade da assinatura" value={cidade} onChange={(e) => setCidade(e.target.value)} fullWidth disabled={!admin} />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Qualificação do representante legal"
              value={qualificacao}
              onChange={(e) => setQualificacao(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              disabled={!admin}
              helperText="Como sai na proposta: nome, nacionalidade, estado civil, profissão, documentos e endereço."
            />
          </Grid>
        </Grid>
        {admin && (
          <Button variant="contained" sx={{ mt: 2 }} disabled={gravando || invalido} onClick={() => void gravar()}>
            {gravando ? "Gravando…" : "Gravar modelo"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
